import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, inr, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, UserCheck, Calendar, Coins } from "lucide-react";

export default function AdminStaff() {
  const [staff, setStaff] = useState([]);
  const [editing, setEditing] = useState(null);
  const [attTab, setAttTab] = useState("team"); // "team" | "attendance" | "points"
  const [att, setAtt] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [grant, setGrant] = useState(null); // { user, amount, note }

  const load = () => {
    api.get("/staff").then(r => setStaff(r.data));
    api.get("/staff/attendance").then(r => setAtt(r.data)).catch(() => {});
    api.get("/admin/users?role=staff").then(r => setStaffUsers(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const save = async () => {
    try {
      const payload = { ...editing, monthly_wage: Number(editing.monthly_wage) };
      if (editing.id) await api.put(`/staff/${editing.id}`, payload);
      else await api.post("/staff", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const mark = async (sid, status) => {
    try {
      await api.post("/staff/attendance", { staff_id: sid, status });
      toast.success(`Marked ${status}`); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const submitGrant = async () => {
    try {
      const amt = Number(grant.amount);
      if (!amt) return toast.error("Enter an amount (negative to debit)");
      await api.post(`/admin/users/${grant.user.id}/grant-points`, { amount: amt, note: grant.note || "" });
      toast.success(`Points ${amt >= 0 ? "credited" : "debited"}`);
      setGrant(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Staff</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">Canteen team & shifts</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex bg-cream-200 rounded-full p-1">
              <button onClick={() => setAttTab("team")} data-testid="tab-team" className={`px-4 py-1.5 rounded-full text-sm font-semibold ${attTab === "team" ? 'bg-ink text-white' : 'text-ink-700'}`}>Team</button>
              <button onClick={() => setAttTab("attendance")} data-testid="tab-attendance" className={`px-4 py-1.5 rounded-full text-sm font-semibold ${attTab === "attendance" ? 'bg-ink text-white' : 'text-ink-700'}`}>Attendance</button>
              <button onClick={() => setAttTab("points")} data-testid="tab-points" className={`px-4 py-1.5 rounded-full text-sm font-semibold ${attTab === "points" ? 'bg-ink text-white' : 'text-ink-700'}`}>Faculty points</button>
            </div>
            <button data-testid="new-staff-btn" onClick={() => setEditing({ name: "", role_in_canteen: "cook", phone: "", shift: "morning", monthly_wage: 0 })}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white font-semibold">
              <Plus size={16} /> New
            </button>
          </div>
        </div>

        {attTab === "team" ? (
          <div className="bg-white rounded-xl border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream-200">
                <tr>
                  <th className="p-3 text-left text-xs font-bold uppercase">Name</th>
                  <th className="p-3 text-left text-xs font-bold uppercase">Role</th>
                  <th className="p-3 text-left text-xs font-bold uppercase">Phone</th>
                  <th className="p-3 text-left text-xs font-bold uppercase">Shift</th>
                  <th className="p-3 text-right text-xs font-bold uppercase">Wage</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {staff.map(s => (
                  <tr key={s.id} className="border-t border-border" data-testid={`staff-row-${s.id}`}>
                    <td className="p-3 font-semibold">{s.name}</td>
                    <td className="p-3"><span className="px-2 py-0.5 rounded-full bg-cream-200 text-xs font-bold uppercase">{s.role_in_canteen}</span></td>
                    <td className="p-3 text-ink-500 font-mono text-xs">{s.phone}</td>
                    <td className="p-3">{s.shift}</td>
                    <td className="p-3 text-right font-bold">{inr(s.monthly_wage)}</td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => mark(s.id, "present")} data-testid={`present-${s.id}`} className="px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-100 text-emerald-900">Present</button>
                        <button onClick={() => mark(s.id, "absent")} data-testid={`absent-${s.id}`} className="px-2.5 py-1 rounded-md text-xs font-bold bg-red-100 text-red-900">Absent</button>
                        <button onClick={() => setEditing(s)} className="px-2.5 py-1 rounded-md text-xs font-bold bg-cream-200">Edit</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : attTab === "attendance" ? (
          <div className="bg-white rounded-xl border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream-200">
                <tr>
                  <th className="p-3 text-left text-xs font-bold uppercase">Date</th>
                  <th className="p-3 text-left text-xs font-bold uppercase">Staff</th>
                  <th className="p-3 text-left text-xs font-bold uppercase">Status</th>
                  <th className="p-3 text-left text-xs font-bold uppercase">Check-in</th>
                </tr>
              </thead>
              <tbody>
                {att.map(a => (
                  <tr key={a.id} className="border-t border-border">
                    <td className="p-3">{a.date}</td>
                    <td className="p-3 font-semibold">{a.staff_name}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${a.status==='present'?'bg-emerald-100 text-emerald-900':a.status==='absent'?'bg-red-100 text-red-900':'bg-amber-100 text-amber-900'}`}>{a.status}</span></td>
                    <td className="p-3 text-ink-500 text-xs">{a.check_in?.slice(11,19) || '—'}</td>
                  </tr>
                ))}
                {att.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-ink-500">No attendance recorded yet — mark someone Present from the Team tab.</td></tr>}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-border overflow-x-auto" data-testid="staff-points-table">
            <div className="p-4 border-b border-border bg-cream-100">
              <div className="text-xs font-bold uppercase tracking-wider text-amber-700">Faculty / staff users (role: staff)</div>
              <div className="text-sm text-ink-600 mt-0.5">Used for QR ordering with points-first checkout. Negative grants = debit.</div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-cream-200">
                <tr>
                  <th className="p-3 text-left text-xs font-bold uppercase">Name</th>
                  <th className="p-3 text-left text-xs font-bold uppercase">Staff ID</th>
                  <th className="p-3 text-left text-xs font-bold uppercase">Phone</th>
                  <th className="p-3 text-right text-xs font-bold uppercase">Wallet</th>
                  <th className="p-3 text-right text-xs font-bold uppercase">Points</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {staffUsers.map(u => (
                  <tr key={u.id} className="border-t border-border" data-testid={`staff-user-${u.id}`}>
                    <td className="p-3 font-semibold">{u.name}</td>
                    <td className="p-3 font-mono text-xs">{u.staff_id || "—"}</td>
                    <td className="p-3 font-mono text-xs text-ink-500">{u.phone || "—"}</td>
                    <td className="p-3 text-right">{inr(u.wallet_balance || 0)}</td>
                    <td className="p-3 text-right font-bold text-amber-700">{(u.points_balance || 0).toFixed(0)}</td>
                    <td className="p-3 text-right">
                      <button data-testid={`grant-points-${u.id}`} onClick={() => setGrant({ user: u, amount: 100, note: "" })}
                        className="px-2.5 py-1 rounded-md text-xs font-bold bg-amber-100 text-amber-900 hover:bg-amber-200 inline-flex items-center gap-1">
                        <Coins size={12}/> Grant
                      </button>
                    </td>
                  </tr>
                ))}
                {staffUsers.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-ink-500">No staff users yet. Register one with role=staff to enable QR/POS staff ordering.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="font-heading text-2xl font-bold">{editing.id ? "Edit" : "New"} staff</div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              {[["name","Name","text"],["role_in_canteen","Role","text"],["phone","Phone","text"],["shift","Shift","text"],["monthly_wage","Wage (₹)","number"]].map(([k,l,t]) => (
                <div key={k} className={k==="name"?"col-span-2":""}>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">{l}</label>
                  <input data-testid={`staff-form-${k}`} type={t} value={editing[k] ?? ""} onChange={e => setEditing({...editing, [k]: e.target.value})}
                    className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
                </div>
              ))}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full font-semibold text-ink-700 hover:bg-cream-200">Cancel</button>
              <button data-testid="staff-form-save" onClick={save} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
      {grant && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4" onClick={() => setGrant(null)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-amber-700 text-xs font-bold uppercase tracking-[0.2em]"><Coins size={14}/> Grant points</div>
            <div className="font-heading text-2xl font-bold text-ink mt-2">{grant.user.name}</div>
            <div className="text-sm text-ink-500 mt-1">Current points: <b>{(grant.user.points_balance || 0).toFixed(0)}</b></div>
            <div className="mt-4">
              <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Amount (negative to debit)</label>
              <input data-testid="grant-amount" type="number" autoFocus value={grant.amount}
                onChange={e => setGrant({...grant, amount: e.target.value})}
                className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
            </div>
            <div className="mt-3">
              <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Note (optional)</label>
              <input data-testid="grant-note" value={grant.note}
                onChange={e => setGrant({...grant, note: e.target.value})}
                placeholder="e.g., Monthly meal allowance"
                className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setGrant(null)} className="px-5 py-2.5 rounded-full font-semibold text-ink-700 hover:bg-cream-200">Cancel</button>
              <button data-testid="grant-submit" onClick={submitGrant} className="px-5 py-2.5 rounded-full bg-amber-500 text-white font-semibold">Apply</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
